/**
 * Lobby3DBoundary — wraps a DECORATIVE lazy 3D scene (Three.js + @tt/shell-3d
 * in its own code-split chunk) so that a chunk-load failure OR a render throw
 * anywhere inside the 3D subtree can never bubble up to the app-level
 * {@link AppErrorBoundary} and black-screen the whole room.
 *
 * On failure we render the optional `fallback` (usually `null`, since these
 * scenes sit on top of the existing 2D TavernBackdrop / standings UI, which
 * stays fully functional underneath). The live WebGL init failure path is
 * already handled INSIDE the scene (it returns null on `createGameShell3D`
 * throw); this boundary is the belt-and-suspenders for everything else.
 */
import { Component } from 'react';
import type { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

export class Lobby3DBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error('[Lobby3DBoundary] decorative 3D scene failed; falling back', error);
  }
  override render(): ReactNode {
    if (this.state.failed) return this.props.fallback ?? null;
    return this.props.children;
  }
}
